/**
 * Pagina lista clienti.
 *
 * Mostra i soggetti con tipo='cliente' del tenant corrente.
 * Wrapper sull'endpoint /api/v1/soggetti?tipo=cliente del core.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '@lib/useUrlState';

interface Cliente {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
  partita_iva: string | null;
  codice_fiscale: string | null;
  email: string | null;
  telefono: string | null;
  pec: string | null;
  codice_sdi: string | null;
  stato: string;
}

/**
 * Pagina lista clienti con ricerca e filtro per stato.
 */
const ClientiList: React.FC = () => {
  const navigate = useNavigate();

  const [clienti, setClienti] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [q, setQ] = useUrlState('q', '');
  const [stato, setStato] = useUrlState('stato', '__all__');

  /**
   * Carica la lista clienti dall'API.
   */
  const caricaClienti = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tipo: 'cliente' });
      if (q.trim()) params.set('q', q.trim());
      if (stato !== '__all__') params.set('stato', stato);

      const url = `/api/v1/soggetti?${params.toString()}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
        },
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      const data: Cliente[] = await response.json();
      setClienti(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [q, stato]);

  useEffect(() => {
    void caricaClienti();
  }, [caricaClienti]);

  /**
   * Restituisce il nome visualizzabile del cliente.
   *
   * @param c - Cliente
   * @returns Nome leggibile
   */
  const displayName = (c: Cliente): string => {
    if (c.ragione_sociale) return c.ragione_sociale;
    if (c.cognome && c.nome) return `${c.cognome} ${c.nome}`;
    return c.cognome ?? c.nome ?? '-';
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Clienti
        </h1>
        <Button onClick={() => navigate('/clienti/nuovo')}>
          Nuovo cliente
        </Button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Filtri */}
      <Card>
        <CardHeader>
          <CardTitle>Filtri</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4">
          <Input
            placeholder="Cerca per nome, ragione sociale, CF"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-72"
          />
          <Select value={stato} onValueChange={setStato}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti</SelectItem>
              <SelectItem value="attivo">Attivo</SelectItem>
              <SelectItem value="disattivato">Disattivato</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          {loading && (
            <p className="p-6 text-center text-sm text-muted-foreground">
              Caricamento...
            </p>
          )}
          {!loading && !error && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome / Ragione sociale</TableHead>
                  <TableHead>P.IVA</TableHead>
                  <TableHead>CF</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Telefono</TableHead>
                  <TableHead>Codice SDI</TableHead>
                  <TableHead>Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clienti.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center text-sm text-muted-foreground"
                    >
                      Nessun cliente trovato
                    </TableCell>
                  </TableRow>
                )}
                {clienti.map((cliente) => (
                  <TableRow
                    key={cliente.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/clienti/${cliente.id}`)}
                  >
                    <TableCell className="font-medium">
                      {displayName(cliente)}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {cliente.partita_iva ?? '-'}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {cliente.codice_fiscale ?? '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {cliente.email ?? '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {cliente.telefono ?? '-'}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {cliente.codice_sdi ?? '-'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          cliente.stato === 'attivo' ? 'default' : 'secondary'
                        }
                      >
                        {cliente.stato === 'attivo' ? 'Attivo' : 'Disattivato'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientiList;
